import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useFocusable, FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import Theme from "./Theme.js";
import Icon from "./IconUtil.js";
import S from './Breadcrumbs.css';
import Breadcrumb from "./Breadcrumb.js";
import { jsx as _jsx } from "react/jsx-runtime";
class Breadcrumbs extends React.Component {
  static contextType = Theme.Context;

  /* eslint-disable react/no-unused-prop-types,react/require-default-props */
  static propTypes = {
    /**
     * Array of crumb objects with a name and optional icon/iconOpts/imageOpts
     */
    crumbs: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      icon: PropTypes.string,
      iconOpts: PropTypes.object
    })),
    /**
     * CSS class to be applied to this component
     */
    className: PropTypes.string,
    /**
     * Theme variant name to be added to the component
     */
    variant: PropTypes.string,
    /**
     * Function to be called when the button is clicked
     */
    onClick: PropTypes.func,
    /**
     * Will be passed through as the data key to the onClick function
     */
    eventData: PropTypes.any,
    /**
     * String or function used to render the sepraotor between Breadcrumb items
     */
    separator: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    /**
     * Icon font class used as an alternative to rendering a string or function as separtor.
     * Preference is given first to a function separator, then separatorIcon, then separator as a string.
     */
    separatorIcon: PropTypes.string,
    /**
     * The number of breakcrumbs that should be rendered
     */
    levels: PropTypes.number
  };
  /* eslint-enable */

  static defaultProps = {
    className: null,
    variant: null,
    onClick: null,
    eventData: null
  };

  /** 
   * (Internal) onClick of the element just adds the eventData prop to the event, and then calls the onClick prop
   */
  onClick = (sourceEvent, crumb, id) => {
    const {
      onClick,
      eventData
    } = this.props;
    if (onClick) {
      onClick({
        data: crumb,
        breadCrumbData: eventData,
        sourceEvent
      });
    }
  };
  render() {
    const theme = this.context;
    const {
      crumbs,
      levels = 100,
      className,
      variant,
      onClick,
      eventData,
      separator,
      separatorIcon,
      layoutOptions,
      focusable = false,
      ...rest
    } = theme.props('Breadcrumbs', this.props); // eslint-disable-line
    const cn = theme.classes('Breadcrumbs', 'breadcrumbs', variant, className, S.content);
    const currCn = theme.subclasses('Breadcrumbs', 'current');
    const linkCn = theme.subclasses('Breadcrumbs', 'link');
    const separatorCn = theme.subclasses('Breadcrumbs', 'separator');
    const start = Math.min(crumbs.length, Math.max(0, crumbs.length - Math.max(1, levels)));
    const use = crumbs.slice(start);
    return /*#__PURE__*/_jsx(FocusableBreadcrumbs, {
      className: cn,
      crumbs: use,
      currentClassName: currCn,
      linkClassName: linkCn,
      onClick: this.onClick,
      separator: separator,
      separatorClassName: separatorCn,
      separatorIcon: separatorIcon,
      focusable: focusable
    });
  }
}
function FocusableBreadcrumbs({
  className,
  crumbs,
  currentClassName,
  linkClassName,
  onClick,
  separator,
  separatorClassName,
  separatorIcon,
  focusable
}) {
  const theme = useContext(Theme);
  const {
    ref,
    focusKey
  } = useFocusable({
    focusable
  });
  return /*#__PURE__*/_jsx(FocusContext.Provider, {
    value: focusKey,
    children: /*#__PURE__*/_jsx("div", {
      className: className,
      ref: ref,
      children: crumbs.map((crumb, idx) => {
        const click = e => onClick(e, crumb, idx);
        const icon = crumb.icon && Icon.create(crumb.icon, crumb.imageOpts, crumb.iconOpts, theme);
        return idx === crumbs.length - 1 ? /*#__PURE__*/_jsx("div", {
          className: currentClassName,
          title: crumb.tooltip,
          children: crumb.name
        }, "curr") : /*#__PURE__*/_jsx(Breadcrumb, {
          crumb: crumb,
          icon: icon,
          index: idx,
          linkClassName: linkClassName,
          onClick: click,
          separator: separator,
          separatorClassName: separatorClassName,
          separatorIcon: separatorIcon
        }, `crumb-${idx}`);
      })
    })
  });
}
export default Breadcrumbs;